import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { InputWrapper } from '../../../common/input-wrapper';
import { decimalValidation } from '../../../../utils/validations';
import { Input } from '../../../common/input.component';
import classNames from 'classnames';
import { trpcSwr } from '../../../../api/trpc';

type Props = {
  close: () => void;
  reject: () => void;
  defaultValues?: Form;
  onSubmit: (data: Form) => Promise<void>;
  title: string;
};

type Form = {
  rate: number;
  fromPrice?: number;
  toPrice?: number;
  goodCode?: string;
};

type FormType = 'code' | 'price';

export const CustomsDeliveryDutyEditModal: React.FC<Props> = ({
  defaultValues,
  onSubmit,
  title,
  ...props
}) => {
  const [type, setType] = useState<FormType>(
    defaultValues?.goodCode ? 'code' : 'price',
  );
  const { mutate: mutateDutyList } = trpcSwr.customsDeliveryDuty.getList.useSWR(
    { type: type },
  );
  const {
    register,
    formState: { errors, isSubmitting },
    setValue,
    handleSubmit,
  } = useForm<Form>({
    defaultValues: defaultValues,
  });

  const intl = useIntl();

  const submit = handleSubmit(async (data) => {
    await onSubmit({
      toPrice: data.toPrice ? +data.toPrice : undefined,
      rate: +data.rate,
      fromPrice: data.fromPrice ? +data.fromPrice : undefined,
      goodCode: data.goodCode,
    });
    mutateDutyList();
    props.close();
  });

  useEffect(() => {
    if (type === 'price') {
      setValue('goodCode', undefined);
    }
    if (type === 'code') {
      setValue('toPrice', undefined);
      setValue('fromPrice', undefined);
    }
  }, [setValue, type]);

  return (
    <div className="modal-dialog">
      <form className="modal-content" onSubmit={submit}>
        <div className="modal-header border-bottom-0 p-4">
          <h3>{title}</h3>
          <button
            type="button"
            className="btn-close"
            onClick={() => props.reject()}
          />
        </div>

        <div className="modal-body pt-2 p-4">
          <div className={'d-flex gap-2 border-bottom border-gray-300 pb-1'}>
            <div
              className={classNames(
                'btn ',
                type === 'price' ? 'btn-success' : 'btn-secondary',
              )}
              onClick={() => {
                setType('price');
              }}
            >
              По стоимости
            </div>
            <div
              className={classNames(
                'btn btn-secondary ',
                type === 'code' ? 'btn-success' : 'btn-secondary',
              )}
              onClick={() => {
                setType('code');
              }}
            >
              По коду
            </div>
          </div>
          <InputWrapper
            title={intl.formatMessage({
              defaultMessage: 'Ставка, ₽',
              id: 'customsDeliveryDutyEditModal.input-label.rate',
              description: 'Заголовок поля ввода',
            })}
            className="mb-3"
            error={errors.rate?.message}
          >
            <Input {...register('rate')} postfix="₽" />
          </InputWrapper>
          {type === 'price' && (
            <>
              <InputWrapper
                title={intl.formatMessage({
                  defaultMessage: 'Стоимость, от ₽',
                  id: 'customsDeliveryDutyEditModal.input-label.fromPrice',
                  description: 'Заголовок поля ввода',
                })}
                className="mb-3"
                error={errors.fromPrice?.message}
              >
                <Input
                  {...register('fromPrice', {
                    ...decimalValidation,
                  })}
                  min={0.01}
                  step={0.01}
                  postfix="₽"
                />
              </InputWrapper>
              <InputWrapper
                title={intl.formatMessage({
                  defaultMessage: 'Стоимость, до ₽',
                  id: 'customsDeliveryDutyEditModal.input-label.toPrice',
                  description: 'Заголовок поля ввода',
                })}
                className="mb-3"
                error={errors.toPrice?.message}
              >
                <Input
                  {...register('toPrice', {
                    ...decimalValidation,
                  })}
                  min={0.1}
                  step={0.1}
                  postfix="₽"
                />
              </InputWrapper>
            </>
          )}
          {type === 'code' && (
            <InputWrapper
              title={intl.formatMessage({
                defaultMessage: 'Код товара',
                id: 'customsDeliveryDutyEditModal.input-label.goodCode',
                description: 'Заголовок поля ввода',
              })}
              className="mb-3"
              error={errors.goodCode?.message}
            >
              <Input {...register('goodCode')} />
            </InputWrapper>
          )}
        </div>
        <div className="modal-footer justify-content-end">
          <button
            type="submit"
            className="btn btn-success"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
            ) : (
              <FormattedMessage
                defaultMessage="Сохранить"
                id="customsDeliveryDutyEditModal.status-label.save"
                description="Надпись на кнопке"
              />
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
